import { create } from 'zustand';
import { type ResumeState, type TemplateId } from '@/types';
import { defaultState, loadState, saveState, getAnonymousId } from '@/lib/utils';

interface ResumeActions extends ResumeState {
  setProfile: (updates: Partial<ResumeState['profile']>) => void;
  setExperience: (id: string, updates: Partial<ResumeState['experience'][0]>) => void;
  addExperience: () => void;
  removeExperience: (id: string) => void;
  setEducation: (id: string, updates: Partial<ResumeState['education'][0]>) => void;
  addEducation: () => void;
  removeEducation: (id: string) => void;
  setSkills: (skills: string[]) => void;
  setTemplate: (template: TemplateId) => void;
  incrementAiUsage: () => void;
  setSubscription: (subscription: ResumeState['aiUsage']['subscription']) => void;
  clearAll: () => void;
}

export const useResumeStore = create<ResumeActions>((set, get) => ({
  ...loadState(),
  setProfile: (updates) => {
    set((state) => ({ profile: { ...state.profile, ...updates } }));
    saveState(get());
  },
  setExperience: (id, updates) => {
    set((state) => ({
      experience: state.experience.map((e) => (e.id === id ? { ...e, ...updates } : e)),
    }));
    saveState(get());
  },
  addExperience: () => {
    set((state) => ({
      experience: [
        ...state.experience,
        { id: `exp-${crypto.randomUUID()}`, company: '', title: '', startDate: '', endDate: '', bullets: [''] },
      ],
    }));
    saveState(get());
  },
  removeExperience: (id) => {
    set((state) => ({ experience: state.experience.filter((e) => e.id !== id) }));
    saveState(get());
  },
  setEducation: (id, updates) => {
    set((state) => ({
      education: state.education.map((e) => (e.id === id ? { ...e, ...updates } : e)),
    }));
    saveState(get());
  },
  addEducation: () => {
    set((state) => ({
      education: [
        ...state.education,
        { id: `edu-${crypto.randomUUID()}`, school: '', degree: '', field: '', graduationDate: '' },
      ],
    }));
    saveState(get());
  },
  removeEducation: (id) => {
    set((state) => ({ education: state.education.filter((e) => e.id !== id) }));
    saveState(get());
  },
  setSkills: (skills) => {
    set({ skills });
    saveState(get());
  },
  setTemplate: (selectedTemplate) => {
    set({ selectedTemplate });
    saveState(get());
  },
  incrementAiUsage: () => {
    set((state) => ({
      aiUsage: { ...state.aiUsage, usedCount: state.aiUsage.usedCount + 1 },
    }));
    saveState(get());
  },
  setSubscription: (subscription) => {
    set((state) => ({
      aiUsage: { ...state.aiUsage, subscription },
    }));
    saveState(get());
  },
  clearAll: () => {
    const next = defaultState();
    next.aiUsage.anonymousId = get().aiUsage.anonymousId || getAnonymousId();
    set(next);
    saveState(get());
  },
}));

export default useResumeStore;
